import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import activityService from '../services/activityService';

interface ActivityState {
  activities: any[];
  recentActivities: any[];
  currentActivity: any | null;
  stats: any;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    totalPages: number;
    total: number;
  };
}

const initialState: ActivityState = {
  activities: [],
  recentActivities: [],
  currentActivity: null,
  stats: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    totalPages: 1,
    total: 0,
  },
};

export const fetchActivities = createAsyncThunk(
  'activities/fetchAll',
  async (filters: any = {}, { rejectWithValue }) => {
    try {
      const response = await activityService.getAll(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch activities');
    }
  }
);

export const fetchRecentActivities = createAsyncThunk(
  'activities/fetchRecent',
  async (limit: number = 10, { rejectWithValue }) => {
    try {
      const response = await activityService.getRecent(limit);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch recent activities');
    }
  }
);

export const fetchActivityStats = createAsyncThunk(
  'activities/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await activityService.getStats();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch activity stats');
    }
  }
);

export const fetchActivityById = createAsyncThunk(
  'activities/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await activityService.getById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch activity');
    }
  }
);

const activitySlice = createSlice({
  name: 'activities',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all activities
      .addCase(fetchActivities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActivities.fulfilled, (state, action) => {
        state.loading = false;
        state.activities = action.payload.data;
        state.pagination = {
          page: action.payload.page,
          totalPages: action.payload.totalPages,
          total: action.payload.total,
        };
      })
      .addCase(fetchActivities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch recent activities
      .addCase(fetchRecentActivities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecentActivities.fulfilled, (state, action) => {
        state.loading = false;
        state.recentActivities = Array.isArray(action.payload)
          ? action.payload
          : (action.payload?.data || action.payload?.activities || []);
      })
      .addCase(fetchRecentActivities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch stats
      .addCase(fetchActivityStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      // Fetch activity by ID
      .addCase(fetchActivityById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActivityById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentActivity = action.payload;
      })
      .addCase(fetchActivityById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = activitySlice.actions;
export default activitySlice.reducer;